/**
 * 매장 팔로우 토글 — "이 매장 소식 받기" (admin#2496 / #885).
 *
 * ## 왜 확인 다이얼로그가 없나
 * 전역 철회(WaitlistMarketingConsentWithdraw)는 연락처 전체의 수신을 끊는 비가역
 * 동작이라 AlertDialog 1회 확인을 거친다. 팔로우는 매장 하나의 가역 선호라 즉시
 * 반영한다 — 두 동작의 무게 차이를 UI 가 드러내야 고객이 "이 매장만 끄면 되는 일"에
 * 전역 철회를 쓰지 않는다(follow 설계 §4.2). 그래서 배치도 철회 링크 **위**다.
 *
 * ## 스위치를 직접 그린다
 * `components/ui/switch` 는 Radix 루트에 라벨을 담을 수 없어 한 행에 라벨+스위치를
 * 두려면 별도 `<label>` 연결이 필요하다. 여기서는 행 전체가 곧 컨트롤이라(모바일
 * 탭 타깃) 버튼 하나에 `role="switch"` 를 주고 트랙·썸을 장식(`aria-hidden`)으로
 * 그린다 — 보조기술에는 스위치 하나와 그 이름·상태만 노출된다.
 *
 * ## 상태 문구는 두 방향 모두 말한다
 * OFF 는 "무엇이 끊기고 무엇이 계속 오는지"(순번 안내는 그대로)를, ON 은 "어디로
 * 무엇이 오는지"를 말한다. 스위치만 있고 설명이 없으면 켜짐이 수신인지 차단인지
 * 고객이 추측해야 한다.
 *
 * ## 진행 중에는 `disabled` 가 아니라 `aria-disabled`
 * `disabled` 는 **포커스를 가진 스위치를 비활성화**해 브라우저가 포커스를 body 로
 * 날린다 — Space 로 막 토글한 키보드·스크린리더 사용자가 바뀐 `aria-checked` 를
 * 고지받지 못하고, 다시 Tab 으로 찾아와야 한다. 중복 제출은 훅의 inFlight 가드가
 * 이미 막으므로 여기서는 포커스를 지키고 클릭만 무시한다.
 *
 * `aria-busy` 는 쓰지 않는다 — 갱신 중인 요소의 부분 변경을 **묶어서 나중에** 읽도록
 * 하는 속성이라, 방금 바뀐 `aria-checked` 고지를 오히려 삼킬 수 있다.
 */
import type { WaitlistStrings } from './strings';

const STORE_FOLLOW_HINT_ID = 'waitlist-store-follow-hint';

interface WaitlistStoreFollowToggleProps {
  t: WaitlistStrings;
  /** 현재 팔로우 여부 — 진행 중에는 낙관값이다. */
  followed: boolean;
  /** 요청 진행 중 — `aria-disabled` 로 알리고 클릭은 무시한다(포커스는 유지). */
  pending: boolean;
  /** 재시도로 풀릴 수 있는 실패. 상태는 누르기 직전으로 되돌아가 있다. */
  failed: boolean;
  /**
   * 409 `STORE_FOLLOW_RECONSENT_REQUIRED` — 토글로는 켤 수 없고 등록 화면의 새 동의로만
   * 풀린다. 일반 오류와 합치면("다시 시도해 주세요") 고객이 켤 방법을 못 찾는다.
   */
  reconsentRequired: boolean;
  onToggle: () => void;
}

export function WaitlistStoreFollowToggle({
  t,
  followed,
  pending,
  failed,
  reconsentRequired,
  onToggle,
}: WaitlistStoreFollowToggleProps) {
  return (
    <div className="shrink-0 px-5 pt-1">
      <div className="rounded-lg border border-border bg-background px-3.5 py-3">
        <button
          type="button"
          role="switch"
          aria-checked={followed}
          aria-describedby={STORE_FOLLOW_HINT_ID}
          aria-disabled={pending}
          data-testid="waitlist-store-follow-toggle"
          onClick={() => {
            if (pending) {
              return;
            }
            onToggle();
          }}
          className={`flex w-full items-center gap-3 bg-transparent p-0 text-start ${
            pending ? 'opacity-60' : ''
          }`}
        >
          <span className="min-w-0 flex-1 text-[13px] font-normal leading-snug text-foreground">
            {t.storeFollowLabel}
          </span>
          {/* 트랙·썸은 장식이다 — 상태는 버튼의 aria-checked 가 말한다. */}
          <span
            aria-hidden="true"
            className={`inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent transition-colors ${
              followed ? 'bg-primary' : 'bg-switch-background'
            }`}
          >
            <span
              className={`block size-4 rounded-full bg-card transition-transform ${
                followed ? 'translate-x-[calc(100%-2px)]' : 'translate-x-0'
              }`}
            />
          </span>
        </button>

        {/*
          OFF 에서만 `waitlist-store-follow-hint` 가 존재한다(계약). ON 의 설명은 같은
          자리에 다른 testid 로 서서, 두 상태 모두 무슨 일이 일어나는지 말해 준다.
        */}
        <p
          id={STORE_FOLLOW_HINT_ID}
          data-testid={followed ? 'waitlist-store-follow-on-hint' : 'waitlist-store-follow-hint'}
          className="m-0 mt-1.5 text-xs leading-snug text-muted-foreground"
        >
          {followed ? t.storeFollowOnHint : t.storeFollowOffHint}
        </p>

        {/*
          재동의 안내가 일반 오류보다 우선한다 — 둘이 동시에 서면 "다시 시도"와 "등록
          때 동의"가 겹쳐 고객이 무엇을 해야 할지 흐려진다.
        */}
        {reconsentRequired ? (
          <p
            role="alert"
            data-testid="waitlist-store-follow-reconsent"
            className="m-0 mt-1.5 text-xs leading-snug text-destructive-dark"
          >
            {t.storeFollowReconsent}
          </p>
        ) : (
          failed && (
            <p
              role="alert"
              data-testid="waitlist-store-follow-error"
              className="m-0 mt-1.5 text-xs leading-snug text-destructive-dark"
            >
              {t.storeFollowError}
            </p>
          )
        )}
      </div>
    </div>
  );
}
